/** Navigation only: model access and publication lineage are verified by the
 * bundle/native APIs. A query parameter must never manufacture that authority. */
import type { UniversalBundle } from '@/shared/api/types';
export type PublishedModelView = 'scene' | 'estimate';
export type MeshStudioRecovery = { jobId: string; pipelineKind: 'pro100_decode' | 'sketchup_decode' };

export function isMeshStudioSourceKind(value: unknown): value is MeshStudioRecovery['pipelineKind'] {
  return value === 'pro100_decode' || value === 'sketchup_decode';
}

export function meshStudioRecoveryFromBundle(bundle: UniversalBundle, modelId: string | null): MeshStudioRecovery | null {
  const lineage = bundle.publicationLineage;
  if (!lineage || typeof lineage !== 'object' || Array.isArray(lineage)) return null;
  const row = lineage as Record<string, unknown>;
  if (row.schema !== 'holzcom.recognition-arm.publication-lineage.v1') return null;
  if (!isMeshStudioSourceKind(row.pipelineKind) || row.pipelineKind !== bundle.sourceKind) return null;
  if (typeof row.modelId !== 'string' || !modelId || row.modelId !== modelId) return null;
  if (typeof row.revisionId !== 'string' || row.revisionId.length === 0) return null;
  if (typeof row.sourceSha256 !== 'string' || !/^[0-9a-f]{64}$/.test(row.sourceSha256)) return null;
  return typeof row.jobId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(row.jobId)
    ? { jobId: row.jobId, pipelineKind: row.pipelineKind }
    : null;
}

/** The authenticated bundle API validates the package/receipt and local files.
 * This is a transported scene, with source-specific binding still intact. It
 * does not recover or impersonate an origin ARM job in the destination. */
export function isImportedNativeSnapshot(bundle: UniversalBundle, modelId: string | null): boolean {
  const snapshot = (bundle as unknown as { nativeProjectSnapshot?: unknown }).nativeProjectSnapshot;
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return false;
  const row = snapshot as Record<string, unknown>;
  const lineage = bundle.publicationLineage as Record<string, unknown> | undefined;
  const origin = row.originLineage as Record<string, unknown> | undefined;
  const native = (bundle.canonicalScene?.scene?.viewerHints as Record<string, unknown> | undefined)?.nativeGeometry as Record<string, unknown> | undefined;
  const digest = (value: unknown) => typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
  return isMeshStudioSourceKind(bundle.sourceKind) && !!modelId && row.modelId === modelId
    && row.schema === 'holzcom.imported-native-snapshot.v1' && row.localPublicationAuthority === false
    && digest(row.receiptSha256) && digest(row.contentSha256) && digest(row.packageSha256)
    && digest(row.glbSha256) && row.glbSha256 === native?.artifactSha256
    && native?.bindingAuthority === 'node_name_and_source_index'
    && !!lineage && lineage.schema === 'holzcom.recognition-arm.publication-lineage.v1'
    && lineage.pipelineKind === bundle.sourceKind && digest(lineage.sourceSha256)
    && digest(lineage.sourceManifestSha256) && digest(lineage.documentSha256)
    && !!origin && typeof origin === 'object' && !Array.isArray(origin)
    && Object.keys(origin).length === Object.keys(lineage).length
    && Object.entries(lineage).every(([key, value]) => origin[key] === value);
}

export function publishedModelEntry(search: string): { modelId: string; view: PublishedModelView } | null {
  const params = new URLSearchParams(search);
  const modelId = params.get('model')?.trim();
  const view = params.get('publishedView');
  return modelId && (view === 'scene' || view === 'estimate') ? { modelId, view } : null;
}

export function openPublishedModel(modelId: string, view: PublishedModelView): void {
  try { window.sessionStorage.setItem('holzcom.mesh-studio.return.v1', `${window.location.pathname}${window.location.search}`); } catch { /* optional navigation history */ }
  const target = new URL('/', window.location.origin);
  target.searchParams.set('model', modelId);
  target.searchParams.set('publishedView', view);
  window.location.assign(target.toString());
}
